import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, errText } from "../../lib/api";
import { Badge, DataTable, KeyRow, OrderLink, Panel, Stat, money, shortDate, stamp, statusTone, useTable } from "./ui";
import OrderDrawer from "./OrderDrawer";
import { AddToNewsletter } from "./NewsletterPage";

export default function CustomerProfile() {
  const { email: raw } = useParams();
  const email = decodeURIComponent(raw);
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const table = useTable("/admin/tables/orders", { buyer_email: email });
  const [sp, setSp] = useSearchParams();
  const openId = sp.get("order");
  const close = () => { const n = Object.fromEntries(sp.entries()); delete n.order; setSp(n, { replace: true }); };

  useEffect(() => {
    api.get("/admin/customer-profile", { params: { email } }).then((r) => setD(r.data)).catch((e) => setError(errText(e)));
  }, [email]);

  const columns = [
    { key: "order_number", label: "Order", render: (r) => <OrderLink number={r.order_number} id={r.id} /> },
    { key: "status", label: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
    { key: "delivery_date", label: "Delivery", sortable: true, render: (r) => shortDate(r.delivery_date) },
    { key: "city", label: "City" },
    { key: "recipient_name", label: "Recipient" },
    { key: "total_mad", label: "Total", sortable: true, render: (r) => <span className="tabular whitespace-nowrap">{money(r.total_mad)} MAD</span> },
    { key: "gateway", label: "Gateway", render: (r) => <Badge tone="ink">{r.gateway}</Badge> },
  ];

  if (error) return <p className="text-danger text-sm" data-testid="customer-profile-error">{error}</p>;
  if (!d) return <p className="text-sm text-muted" data-testid="customer-profile-loading">Loading the customer…</p>;
  const k = d.kpis;

  return (
    <div className="space-y-5" data-testid="customer-profile">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h1 className="font-display text-3xl text-ink" data-testid="customer-profile-email">{d.customer.email}</h1>
          <p className="text-sm text-muted">{d.customer.name || "Guest buyer"} · {d.customer.country} · pays in {d.customer.currency}</p>
          <div className="flex gap-1 mt-1">
            <Badge tone={d.customer.has_account ? "good" : "plain"}>{d.customer.has_account ? "has an account" : "guest checkout"}</Badge>
            {k.plans ? <Badge tone="ink">{k.plans} recurring plan(s)</Badge> : null}
          </div>
          <div className="mt-2">
            <AddToNewsletter email={d.customer.email} testid="customer-add-to-newsletter" />
          </div>
        </div>
        <div className="ms-auto flex flex-wrap items-center gap-2">
          {d.customer.has_account ? (
            <button type="button" className="btn-primary text-xs" data-testid="customer-impersonate"
                    onClick={async () => {
                      setError("");
                      try {
                        const { data } = await api.post(`/admin/customers/${encodeURIComponent(email)}/impersonate`);
                        // Keep the admin's own token so one click brings us straight back.
                        const adminTok = localStorage.getItem("fl_token");
                        if (adminTok) localStorage.setItem("fl_admin_token", adminTok);
                        localStorage.setItem("fl_impersonating_customer", JSON.stringify({
                          email: data.customer.email, name: data.customer.name,
                        }));
                        localStorage.setItem("fl_token", data.token);
                        window.location.href = "/en/account";
                      } catch (e) { setError(errText(e)); }
                    }}>
              Sign in as this customer →
            </button>
          ) : null}
          <Link to="/admin/customers" className="btn-ghost text-xs">← All customers</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat testid="customer-kpi-orders" label="Orders" value={k.orders} />
        <Stat testid="customer-kpi-ltv" label="Lifetime value" value={`${money(k.ltv_mad)} MAD`} />
        <Stat testid="customer-kpi-aov" label="Average order" value={`${money(k.aov_mad)} MAD`} />
        <Stat testid="customer-kpi-refunds" label="Refunded" value={`${money(k.refunds_mad)} MAD`} tone={k.refunds_mad ? "warn" : undefined} />
        <Stat testid="customer-kpi-dates" label="First → last" value={shortDate((k.first_order || "").slice(0, 10))} hint={`last ${shortDate((k.last_order || "").slice(0, 10))}`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Panel title="Contact" testid="customer-contact">
          <KeyRow label="Email">{d.customer.email}</KeyRow>
          <KeyRow label="Phone">{d.customer.phone}</KeyRow>
          <KeyRow label="Country">{d.customer.country}</KeyRow>
          <KeyRow label="Joined">{d.customer.joined ? stamp(d.customer.joined) : "—"}</KeyRow>
          <KeyRow label="Cities sent to">{d.cities.map((c) => `${c.city} (${c.orders})`).join(", ")}</KeyRow>
          <KeyRow label="Codes used">{d.discount_codes.length ? d.discount_codes.map((c) => `${c.code} ×${c.used}`).join(", ") : "—"}</KeyRow>
        </Panel>

        <Panel title="Saved recipients" testid="customer-recipients">
          {d.recipients.length ? d.recipients.map((r, i) => (
            <p key={`${r.name}-${r.phone}-${i}`} className="text-sm border-b border-brass-light py-1.5">{r.name} · {r.phone}<span className="block text-xs text-muted">{r.address}</span></p>
          )) : <p className="text-sm text-muted">None saved.</p>}
        </Panel>

        <Panel title="Recurring plans" testid="customer-plans">
          {d.subscriptions.length ? d.subscriptions.map((s) => (
            <p key={s.id} className="text-sm border-b border-brass-light py-1.5 flex gap-2">
              <Link className="underline decoration-brass" to={`/admin/subscriptions?q=${s.subscription_number}`}>{s.subscription_number}</Link>
              <Badge tone={statusTone(s.status)}>{s.status}</Badge>
              <span className="ms-auto tabular">{s.symbol} {s.amount} · {s.plan}</span>
            </p>
          )) : <p className="text-sm text-muted">No plan yet.</p>}
        </Panel>
      </div>

      <Panel title="Reviews left" testid="customer-reviews">
        {d.reviews.length ? d.reviews.map((r) => (
          <p key={r.id} className="text-sm border-b border-brass-light py-1.5">
            <span className="tabular">{"★".repeat(r.rating)}</span> <Badge tone={statusTone(r.status)}>{r.status}</Badge> {(r.body || "").slice(0, 160)}
          </p>
        )) : <p className="text-sm text-muted">None yet.</p>}
      </Panel>

      <Panel title="Messages sent to this buyer" testid="customer-messages">
        {d.notifications.length ? d.notifications.map((n, i) => (
          <p key={`${n.created_at}-${n.kind}-${i}`} className="text-xs border-b border-brass-light py-1 flex gap-2">
            <span className="tabular text-muted">{stamp(n.created_at)}</span>
            <Badge tone={n.success ? "good" : "bad"}>{n.channel}</Badge>
            <span>{n.kind}</span><span className="ms-auto text-muted">{n.detail}</span>
          </p>
        )) : <p className="text-sm text-muted">Nothing sent.</p>}
      </Panel>

      <div className="space-y-2">
        <h3 className="smallcaps">Every order</h3>
        <DataTable table={table} columns={columns} testid="customer-orders-table" name={`orders-${email}`} searchPlaceholder="Order or recipient…" />
      </div>
      {openId ? <OrderDrawer orderId={openId} onClose={close} onChanged={table.reload} /> : null}
    </div>
  );
}
